import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { CalendarDays, FileText, Users, Briefcase, CreditCard, Home, User, Building, UserPlus, Info, Shield, Loader2, AlertTriangle } from "lucide-react";
import PersonalInfoSection from "../components/sections/PersonalInfoSection";
import EmployerInfoSection from "../components/sections/EmployerInfoSection";
import IncomeSection from "../components/sections/IncomeSection";
import DebtSection from "../components/sections/DebtSection";
import CodebitorSection from "../components/sections/CodebitorSection";
import ConsumerInformationSection from "../components/sections/ConsumerInformationSection";
import GDPRInformationSection from "../components/sections/GDPRInformationSection";
import ConsumerDeclarationSection from "../components/sections/ConsumerDeclarationSection";
import BankAffiliatedPersonsSection from "../components/sections/BankAffiliatedPersonsSection";
import { mockFormData, createEmptyCodebitor } from "../utils/mockData";
import { useToast } from "../hooks/use-toast";
import { YesNoRow } from "../components/ui/yes-no-toggle";

const PROPERTY_CREDIT_TYPES = ["Credit Ipotecar", "Refinanțare Credit Ipotecar", "Credit Noua Casă", "Achiziție teren", "Credit de construcție"];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PublicLoanForm = () => {
  const { slug, inviteId, codebitorLinkId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isCodebitorMode = !!codebitorLinkId;
  const isSignMode = location.pathname.endsWith('/semnaturi');
  const { toast } = useToast();
  
  const [formData, setFormData] = useState(mockFormData);
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingOffers, setIsGeneratingOffers] = useState(false);
  const [brokerData, setBrokerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [codebitorInfo, setCodebitorInfo] = useState(null); // {linkId, applicantName, groupId}
  const [generatedCodebitorLink, setGeneratedCodebitorLink] = useState(null);

  const steps = [
    { id: 0, title: "Informare OUG 52", icon: Info },
    { id: 11, title: "Declarație Consumator", icon: CalendarDays },
    { id: 1, title: "Protecția Datelor", icon: Shield },
    { id: 2, title: "Tip Credit", icon: CreditCard },
    { id: 3, title: "Informații Credit", icon: FileText },
    { id: 4, title: "Tip Imobil", icon: Home },
    { id: 5, title: "Date Personale", icon: User },
    { id: 6, title: "Persoane Afiliate", icon: Building },
    { id: 7, title: "Date Angajator", icon: Building },
    { id: 8, title: "Venituri", icon: Briefcase },
    { id: 9, title: "Datorii", icon: Users },
    { id: 10, title: "Codebitori", icon: UserPlus }
  ];

  // Pași pentru fluxul „doar semnături" (client care nu completează formularul financiar)
  const signSteps = [
    { id: 200, title: "Date Identificare", icon: User },
    { id: 0, title: "Informare OUG 52", icon: Info },
    { id: 11, title: "Declarație Consumator", icon: CalendarDays },
    { id: 1, title: "Protecția Datelor", icon: Shield }
  ];

  // În modul codebitor, ascundem pasul „Codebitori" (un codebitor nu adaugă alți codebitori)
  const visibleSteps = isSignMode
    ? signSteps
    : (isCodebitorMode ? steps.filter(s => s.id !== 10) : steps);
  const currentVisibleIndex = Math.max(0, visibleSteps.findIndex(s => s.id === currentStep));
  const goToVisibleIndex = (i) => {
    const idx = Math.max(0, Math.min(visibleSteps.length - 1, i));
    setCurrentStep(visibleSteps[idx].id);
  };

  // La intrarea în modul „doar semnături", pornim de la primul pas al acestui flux
  useEffect(() => {
    if (isSignMode) setCurrentStep(200);
  }, [isSignMode]);

  // Încarcă datele brokerului la montarea componentei
  useEffect(() => {
    const loadBrokerData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let response;
        if (codebitorLinkId) {
          // Formular pentru CODEBITOR (link generat de solicitant)
          response = await axios.get(`${BACKEND_URL}/api/public/codebitor-link/${codebitorLinkId}`);
          setBrokerData(response.data.broker);
          setCodebitorInfo({
            linkId: codebitorLinkId,
            applicantName: response.data.applicantName,
            groupId: response.data.groupId
          });
        } else if (inviteId) {
          // Link unic (invitație)
          response = await axios.get(`${BACKEND_URL}/api/public/invite/${inviteId}`);
          setBrokerData(response.data.broker);
          setInviteInfo({
            inviteId: inviteId,
            clientName: response.data.clientName,
            expiresAt: response.data.expiresAt
          });
        } else if (slug) {
          // Link public al brokerului
          response = await axios.get(`${BACKEND_URL}/api/public/broker/${slug}`);
          setBrokerData(response.data);
        } else {
          throw new Error("Link invalid");
        }
      } catch (err) {
        console.error("Eroare la încărcarea datelor:", err);
        if (err.response?.status === 404) {
          setError("Acest link nu este valid sau intermediarul nu există.");
        } else if (err.response?.status === 410) {
          setError(err.response.data?.detail || "Această invitație a expirat sau a fost deja folosită.");
        } else {
          setError("A apărut o eroare la încărcarea formularului. Vă rugăm încercați din nou.");
        }
      } finally {
        setLoading(false);
      }
    };

    loadBrokerData();
  }, [slug, inviteId, codebitorLinkId]);

  const updateFormData = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: Array.isArray(data) ? data : { ...prev[section], ...data }
    }));
  };

  // Copy of validateCurrentStep from LoanForm.jsx
  const validateCurrentStep = () => {
    switch (currentStep) {
      case 200:
        if (!formData.personalInfo.firstName || !formData.personalInfo.lastName) {
          return { isValid: false, message: "Numele și prenumele sunt obligatorii." };
        }
        if (!formData.personalInfo.domCity || !formData.personalInfo.domStreet) {
          return { isValid: false, message: "Localitatea și strada de domiciliu sunt obligatorii." };
        }
        return { isValid: true };
      case 0:
        if (!formData.consumerInformation.acceptInformation ||
            !formData.consumerInformation.acceptDataProcessing ||
            !formData.consumerInformation.acceptTerms) {
          return { isValid: false, message: "Toate confirmările sunt obligatorii pentru a continua." };
        }
        return { isValid: true };
      
      case 1:
        if (!formData.gdprInformation.gdprInformationRead ||
            !formData.gdprInformation.gdprConsentProcessing ||
            !formData.gdprInformation.gdprRightsUnderstood) {
          return { isValid: false, message: "Toate confirmările GDPR sunt obligatorii." };
        }
        return { isValid: true };
      
      case 2:
        if (formData.creditType.selectedTypes.length === 0) {
          return { isValid: false, message: "Selectați cel puțin un tip de credit." };
        }
        return { isValid: true };
      
      case 3:
        if (!formData.creditInfo.creditValue || !formData.creditInfo.creditPeriod || !formData.creditInfo.currency) {
          return { isValid: false, message: "Valoarea, perioada și moneda sunt obligatorii." };
        }
        return { isValid: true };
      
      case 4:
        if (!formData.propertyInfo.propertyType || !formData.propertyInfo.energyClass) {
          return { isValid: false, message: "Tipul imobilului și clasa energetică sunt obligatorii." };
        }
        {
          const showHousing = (formData.creditType?.selectedTypes || []).some(t => PROPERTY_CREDIT_TYPES.includes(t));
          if (showHousing) {
            const housingQuestions = {
              firstProperty: "Este primul imobil achiziționat prin credit?",
              ownsHomeAtRequest: "Dețin în proprietate o locuință la data solicitării",
              ownedHomeAtRequest: "La data solicitării am deținut în proprietate o locuință",
              willLiveInProperty: "Voi locui în imobilul finanțat"
            };
            const unanswered = Object.keys(housingQuestions).filter(k => formData.propertyInfo[k] === null || formData.propertyInfo[k] === undefined);
            if (unanswered.length > 0) {
              return { isValid: false, message: `Răspundeți la toate întrebările Da/Nu: ${unanswered.map(k => housingQuestions[k]).join(", ")}` };
            }
          }
        }
        return { isValid: true };
      
      case 5:
        const fieldLabels = {
          firstName: "Prenume", lastName: "Nume", birthDate: "Data Nașterii", cnp: "CNP", sex: "Sex",
          idSeries: "Serie Act", idNumber: "Număr Act", idIssuer: "Emitent",
          idIssueDate: "Data Eliberare", idExpiryDate: "Data Expirare",
          domCounty: "Județ/Sector (domiciliu)", domStreet: "Strada (domiciliu)", domNumber: "Nr. (domiciliu)",
          phone: "Telefon", email: "Email"
        };
        const requiredPersonalFields = Object.keys(fieldLabels);
        const missingFields = requiredPersonalFields.filter(f => !formData.personalInfo[f] || formData.personalInfo[f].trim() === "");
        if (missingFields.length > 0) {
          const missingNames = missingFields.map(f => fieldLabels[f]).join(", ");
          const errMap = {};
          missingFields.forEach(f => { errMap[f] = true; });
          return { isValid: false, message: `Câmpuri obligatorii necompletate: ${missingNames}`, errorFields: errMap };
        }
        if (formData.personalInfo.isPoliticallyExposed === null || formData.personalInfo.isPoliticallyExposed === undefined) {
          return { isValid: false, message: "Răspundeți la întrebarea Persoană Expusă Politic (PEP)." };
        }
        return { isValid: true };
      
      case 6:
        {
          const bankQuestions = {
            isBankFamilyMember: "Membru de familie angajat al băncii?",
            hasGroupRelations: "Relații de grup?",
            isAffiliatedPerson: "Persoană aflată în relații speciale cu banca?"
          };
          const unanswered = Object.keys(bankQuestions).filter(k => formData.bankAffiliatedPersons[k] === null || formData.bankAffiliatedPersons[k] === undefined);
          if (unanswered.length > 0) {
            return { isValid: false, message: `Răspundeți la toate întrebările Da/Nu: ${unanswered.map(k => bankQuestions[k]).join(", ")}` };
          }
        }
        if (formData.bankAffiliatedPersons.isBankFamilyMember && 
            (!formData.bankAffiliatedPersons.bankName || !formData.bankAffiliatedPersons.employeeName)) {
          return { isValid: false, message: "Specificați banca și numele angajatului." };
        }
        return { isValid: true };
      
      case 7:
        const empLabels = {
          companyName: "Nume Companie", cui: "CUI", hireDate: "Data Angajării",
          totalSeniority: "Vechime Totală", profession: "Profesia", currentPosition: "Funcția Actuală", caenCode: "Cod CAEN"
        };
        const requiredEmployerFields = Object.keys(empLabels);
        const missingEmployer = requiredEmployerFields.filter(f => !formData.employerInfo[f] || formData.employerInfo[f].toString().trim() === "");
        if (missingEmployer.length > 0) {
          const missingEmpNames = missingEmployer.map(f => empLabels[f]).join(", ");
          const empErrMap = {};
          missingEmployer.forEach(f => { empErrMap[f] = true; });
          return { isValid: false, message: `Câmpuri obligatorii necompletate: ${missingEmpNames}`, errorFields: empErrMap };
        }
        if (formData.employerInfo.hasInterruptions === null || formData.employerInfo.hasInterruptions === undefined) {
          return { isValid: false, message: "Răspundeți la întrebarea privind întreruperile de activitate." };
        }
        return { isValid: true };
      
      case 8:
        const hasValidIncome = formData.income.incomes.some(i => i.type && i.value && parseFloat(i.value) > 0);
        if (!hasValidIncome) {
          return { isValid: false, message: "Introduceți cel puțin un venit valid." };
        }
        if (formData.income.incomes.length === 1 && !formData.income.confirmedSingleIncome) {
          return { isValid: false, message: "Confirmați că acesta este singurul venit sau adăugați alte venituri." };
        }
        if (formData.income.foreignIncome === null || formData.income.foreignIncome === undefined ||
            formData.income.ownCompanyIncome === null || formData.income.ownCompanyIncome === undefined) {
          return { isValid: false, message: "Răspundeți la întrebările Da/Nu privind veniturile din străinătate și din firmă proprie." };
        }
        return { isValid: true };
      
      case 9:
        return { isValid: true }; // Datoriile sunt opționale
      
      case 10:
        return { isValid: true }; // Codebitorii sunt opționali
      
      case 11:
        if (!formData.consumerDeclaration.reduceReflectionPeriod && !formData.consumerDeclaration.noReductionReflectionPeriod) {
          return { isValid: false, message: "Alegeți o opțiune pentru perioada de reflecție." };
        }
        if (!formData.consumerDeclaration.communicateByEmail && !formData.consumerDeclaration.communicateInWriting) {
          return { isValid: false, message: "Alegeți cel puțin o metodă de comunicare." };
        }
        if (!formData.consumerDeclaration.finalDataAccuracy || !formData.consumerDeclaration.finalDataProcessingConsent || 
            !formData.consumerDeclaration.finalNoFirmOffer) {
          return { isValid: false, message: "Bifați toate confirmările finale obligatorii." };
        }
        if (!formData.consumerDeclaration.consumerSignature?.trim()) {
          return { isValid: false, message: "Semnătura electronică este obligatorie." };
        }
        return { isValid: true };
      
      default:
        return { isValid: true };
    }
  };

  const handleNext = () => {
    const validation = validateCurrentStep();
    if (!validation.isValid) {
      setFieldErrors(validation.errorFields || {});
      toast({
        title: "Câmpuri incomplete",
        description: validation.message,
        variant: "destructive"
      });
      return;
    }
    setFieldErrors({});
    goToVisibleIndex(currentVisibleIndex + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateCurrentStep();
    if (!validation.isValid) {
      toast({
        title: "Formular incomplet",
        description: validation.message,
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/process-form`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brokerData: brokerData,
          formData: formData,
          inviteId: inviteInfo?.inviteId,
          groupId: isCodebitorMode ? codebitorInfo?.groupId : formData.applicationGroupId,
          role: isCodebitorMode ? 'codebitor' : 'applicant',
          codebitorLinkId: isCodebitorMode ? codebitorInfo?.linkId : undefined,
          applicantName: isCodebitorMode ? codebitorInfo?.applicantName : undefined,
          signaturesOnly: isSignMode,
          brokerSlug: slug || undefined
        }),
      });

      if (!response.ok) {
        let errMsg = `Serverul a răspuns cu eroare (${response.status}). Vă rugăm încercați din nou.`;
        try {
          const errorData = await response.json();
          errMsg = errorData.detail || errMsg;
        } catch (parseErr) { /* răspuns non-JSON (ex: pagină HTML de la gateway) */ }
        throw new Error(typeof errMsg === 'string' ? errMsg : 'Eroare la procesarea formularului');
      }

      // Marchează invitația ca folosită (dacă e cazul)
      if (inviteInfo?.inviteId) {
        try {
          await axios.post(`${BACKEND_URL}/api/public/invite/${inviteInfo.inviteId}/mark-used`);
        } catch (err) {
          console.warn("Nu s-a putut marca invitația ca folosită:", err);
        }
      }

      toast({
        title: "✅ Succes!",
        description: "Formularul a fost trimis cu succes! PDF-ul a fost generat.",
        variant: "default"
      });

      // Redirect sau mesaj de mulțumire
      setTimeout(() => {
        setCurrentStep(0);
        setFormData(mockFormData);
      }, 3000);

    } catch (error) {
      console.error('Eroare:', error);
      toast({
        title: "❌ Eroare",
        description: error.message || "A apărut o eroare. Încercați din nou.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addCodebitor = () => {
    const newCodebitor = createEmptyCodebitor();
    updateFormData('codebitors', { 
      hasCodebitors: true,
      codebitorsList: [...formData.codebitors.codebitorsList, newCodebitor]
    });
  };

  const handleAutoOffer = async () => {
    setIsGeneratingOffers(true);
    try {
      const codebitorsList = formData.codebitors?.codebitorsList || [];
      const incomes = [
        ...(formData.income?.incomes || []).map(i => parseFloat(i.value) || 0),
        ...codebitorsList.flatMap(c => (c.income?.incomes || []).map(i => parseFloat(i.value) || 0))
      ].filter(v => v > 0);
      const mapDebt = (d) => ({
        monthlyRate: parseFloat(d.monthlyRate) || 0,
        currency: d.rateCurrency || d.currency || "LEI",
        willRefinance: !!d.willRefinance
      });
      const debts = [
        ...(formData.debts || []).map(mapDebt),
        ...codebitorsList.flatMap(c => (c.debts || []).map(mapDebt))
      ].filter(d => d.monthlyRate > 0);

      const periodValue = parseFloat(formData.creditInfo.creditPeriod) || 30;
      const periodMonths = formData.creditInfo.periodUnit === 'luni' ? Math.round(periodValue) : Math.round(periodValue * 12);

      const payload = {
        // Intermediarul se identifică DOAR pe server, din DB, pe baza acestor câmpuri
        brokerSlug: slug || undefined,
        inviteId: inviteInfo?.inviteId || inviteId || undefined,
        codebitorLinkId: isCodebitorMode ? codebitorInfo?.linkId : undefined,
        clientName: `${formData.personalInfo.firstName || ""} ${formData.personalInfo.lastName || ""}`.trim(),
        clientPhone: formData.personalInfo.phone || "",
        clientEmail: formData.personalInfo.email || "",
        sex: formData.personalInfo.sex || "",
        birthDate: formData.personalInfo.birthDate || "",
        cnp: formData.personalInfo.cnp || "",
        firstProperty: !!formData.propertyInfo.firstProperty,
        energyClass: formData.propertyInfo.energyClass || "B",
        propertyType: formData.propertyInfo.propertyType || "",
        creditValue: parseFloat(formData.creditInfo.creditValue) || 0,
        creditCurrency: formData.creditInfo.currency || "LEI",
        creditPeriodMonths: periodMonths,
        propertyPrice: parseFloat(formData.creditInfo.propertyPrice) || 0,
        downPayment: parseFloat(formData.creditInfo.downPayment) || 0,
        incomes,
        debts
      };

      const response = await fetch(`${BACKEND_URL}/api/auto-offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        let errMsg = `Serverul a răspuns cu eroare (${response.status}). Vă rugăm încercați din nou.`;
        try {
          const err = await response.json();
          errMsg = err.detail || errMsg;
        } catch (parseErr) { /* răspuns non-JSON */ }
        throw new Error(typeof errMsg === 'string' ? errMsg : 'Eroare la generarea ofertelor');
      }
      const result = await response.json();
      const s = result.summary || {};
      toast({
        title: "✅ Oferte generate!",
        description: `Capacitate: ${Math.round(s.available_payment || 0).toLocaleString('ro-RO')} LEI/lună (DSTI ${s.dsti_percent}%). Sumă maximă: ${Math.round(s.max_capacity || 0).toLocaleString('ro-RO')} LEI. Ofertele au fost trimise brokerului, care vă va contacta.`,
      });
    } catch (error) {
      toast({ title: "❌ Eroare", description: error.message, variant: "destructive" });
    } finally {
      setIsGeneratingOffers(false);
    }
  };

  const removeCodebitor = (codebitorId) => {
    const newList = formData.codebitors.codebitorsList.filter(c => c.id !== codebitorId);
    updateFormData('codebitors', { hasCodebitors: newList.length > 0, codebitorsList: newList });
  };

  const updateCodebitor = (codebitorId, updatedData) => {
    const newList = formData.codebitors.codebitorsList.map(c => c.id === codebitorId ? updatedData : c);
    updateFormData('codebitors', { hasCodebitors: newList.length > 0, codebitorsList: newList });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Se încarcă formularul...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-red-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Link Invalid</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate("/")} variant="outline">
              Înapoi la pagina principală
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Construiește header-ul cu branding-ul brokerului
  const headerColor = brokerData?.headerColor || "#2563eb";

  // Render sections (same as LoanForm)
  const renderCreditTypeSection = () => (
    <Card className="mb-6">
      <CardHeader style={{ background: `linear-gradient(to right, ${headerColor}15, ${headerColor}05)` }} className="border-b">
        <CardTitle className="flex items-center gap-2" style={{ color: headerColor }}>
          <CreditCard className="h-5 w-5" />
          Tipul de Credit Solicitat
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {["Credit Ipotecar", "Credit Nevoi Personale", "Refinanțare Credit Nevoi Personale", "Refinanțare Credit Ipotecar",
            "Credit Noua Casă", "Credit Imobiliar", "Credit de construcție", "Achiziție teren", "Credit Auto", "Card de Credit"
          ].map((type) => (
            <div key={type} className="flex items-center space-x-2">
              <Checkbox 
                id={type}
                checked={formData.creditType.selectedTypes.includes(type)}
                onCheckedChange={(checked) => {
                  const newTypes = checked 
                    ? [...formData.creditType.selectedTypes, type]
                    : formData.creditType.selectedTypes.filter(t => t !== type);
                  updateFormData('creditType', { selectedTypes: newTypes });
                }}
              />
              <Label htmlFor={type} className="text-sm font-medium text-gray-700">{type}</Label>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  const renderCreditInfoSection = () => (
    <Card className="mb-6">
      <CardHeader style={{ background: `linear-gradient(to right, ${headerColor}15, ${headerColor}05)` }} className="border-b">
        <CardTitle className="flex items-center gap-2" style={{ color: headerColor }}>
          <FileText className="h-5 w-5" />
          Informații despre Creditul Solicitat
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Valoarea Creditului</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                value={formData.creditInfo.creditValue}
                onChange={(e) => updateFormData('creditInfo', { creditValue: e.target.value })}
                className="flex-1"
              />
              <Select value={formData.creditInfo.currency} onValueChange={(v) => updateFormData('creditInfo', { currency: v })}>
                <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="LEI">LEI</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Perioada Creditului</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                value={formData.creditInfo.creditPeriod}
                onChange={(e) => updateFormData('creditInfo', { creditPeriod: e.target.value })}
                className="flex-1"
              />
              <Select value={formData.creditInfo.periodUnit} onValueChange={(v) => updateFormData('creditInfo', { periodUnit: v })}>
                <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ani">Ani</SelectItem>
                  <SelectItem value="luni">Luni</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Preț Imobil Achiziționat (LEI)</Label>
            <Input
              type="number"
              data-testid="credit-property-price"
              value={formData.creditInfo.propertyPrice || ""}
              onChange={(e) => updateFormData('creditInfo', { propertyPrice: e.target.value })}
              placeholder="Prețul imobilului"
            />
          </div>
          <div className="space-y-2">
            <Label>Avans Disponibil (LEI)</Label>
            <Input
              type="number"
              data-testid="credit-down-payment"
              value={formData.creditInfo.downPayment || ""}
              onChange={(e) => updateFormData('creditInfo', { downPayment: e.target.value })}
              placeholder="Suma avansului disponibil"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderPropertySection = () => (
    <Card className="mb-6">
      <CardHeader style={{ background: `linear-gradient(to right, ${headerColor}15, ${headerColor}05)` }} className="border-b">
        <CardTitle className="flex items-center gap-2" style={{ color: headerColor }}>
          <Home className="h-5 w-5" />
          Tipul și Detaliile Imobilului
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Ce se va achiziționa?</Label>
            <Select value={formData.propertyInfo.propertyType} onValueChange={(v) => updateFormData('propertyInfo', { propertyType: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Apartament">Apartament</SelectItem>
                <SelectItem value="Casă">Casă</SelectItem>
                <SelectItem value="Teren">Teren</SelectItem>
                <SelectItem value="Alte tipuri">Alte tipuri</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Clasa Energetică</Label>
            <Select value={formData.propertyInfo.energyClass} onValueChange={(v) => updateFormData('propertyInfo', { energyClass: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["A", "B", "C", "D", "Necunoscut"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1 md:col-span-2 space-y-1 border-t pt-4 mt-2">
            {(() => {
              const showHousing = (formData.creditType?.selectedTypes || []).some(t => PROPERTY_CREDIT_TYPES.includes(t));
              if (!showHousing) return (
                <p className="text-sm text-gray-400 italic">Întrebările despre locuință se aplică creditelor imobiliare (achiziție, refinanțare ipotecar, Noua Casă, teren, construcție).</p>
              );
              return (
                <div className="divide-y">
                  <YesNoRow
                    label="Este primul imobil achiziționat prin credit? (prima locuință)"
                    value={formData.propertyInfo.firstProperty ?? null}
                    onChange={(v) => updateFormData('propertyInfo', { firstProperty: v })}
                    testid="first-property"
                  />
                  <YesNoRow
                    label="Dețin în proprietate o locuință la data solicitării creditului"
                    value={formData.propertyInfo.ownsHomeAtRequest ?? null}
                    onChange={(v) => updateFormData('propertyInfo', { ownsHomeAtRequest: v })}
                    testid="owns-home-at-request"
                  />
                  <YesNoRow
                    label="La data solicitării creditului am deținut în proprietate o locuință"
                    value={formData.propertyInfo.ownedHomeAtRequest ?? null}
                    onChange={(v) => updateFormData('propertyInfo', { ownedHomeAtRequest: v })}
                    testid="owned-home-at-request"
                  />
                  <YesNoRow
                    label="Voi locui în imobilul finanțat prin creditul solicitat"
                    value={formData.propertyInfo.willLiveInProperty ?? null}
                    onChange={(v) => updateFormData('propertyInfo', { willLiveInProperty: v })}
                    testid="will-live-in-property"
                  />
                </div>
              );
            })()}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const handleGenerateCodebitorLink = async () => {
    const pi = formData.personalInfo || {};
    const applicantName = `${pi.firstName || ""} ${pi.lastName || ""}`.trim();
    if (!applicantName) {
      toast({
        title: "Nume solicitant lipsă",
        description: "Completează întâi numele și prenumele solicitantului (pasul Date Personale) înainte de a genera linkul de codebitor.",
        variant: "destructive"
      });
      return;
    }
    try {
      let groupId = formData.applicationGroupId;
      if (!groupId) {
        groupId = (window.crypto?.randomUUID?.() || `grp-${Date.now()}-${Math.random().toString(36).slice(2)}`);
        setFormData(prev => ({ ...prev, applicationGroupId: groupId }));
      }
      const resp = await axios.post(`${BACKEND_URL}/api/public/codebitor-link`, {
        brokerSlug: slug || undefined,
        inviteId: inviteId || undefined,
        groupId,
        applicantName
      });
      const path = resp.data.path || `/apply/codebitor/${resp.data.linkId}`;
      setGeneratedCodebitorLink(`${window.location.origin}${path}`);
      toast({ title: "Link generat", description: "Copiază linkul și trimite-l codebitorului." });
    } catch (err) {
      toast({ title: "Eroare", description: err.response?.data?.detail || "Nu s-a putut genera linkul.", variant: "destructive" });
    }
  };

  const renderCodebitorsSection = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader style={{ background: `linear-gradient(to right, ${headerColor}15, ${headerColor}05)` }} className="border-b">
          <CardTitle className="flex items-center gap-2" style={{ color: headerColor }}>
            <UserPlus className="h-5 w-5" />
            Codebitori (Opțional)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="hasCodebitors"
                checked={formData.codebitors.hasCodebitors}
                onCheckedChange={(checked) => {
                  updateFormData('codebitors', { hasCodebitors: checked, codebitorsList: checked ? [] : [] });
                }}
              />
              <Label htmlFor="hasCodebitors" className="text-lg font-medium">
                Acest credit are codebitor(i)
              </Label>
            </div>

            <div className="p-4 rounded-lg border border-indigo-200 bg-indigo-50 space-y-3" data-testid="codebitor-link-panel">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-medium text-indigo-900">Trimite un link codebitorului</p>
                  <p className="text-sm text-indigo-700">Codebitorul completează singur toate datele (identic cu solicitantul), prin linkul de mai jos.</p>
                </div>
                <Button type="button" onClick={handleGenerateCodebitorLink} className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0" data-testid="generate-codebitor-link-btn">
                  Generează link codebitor
                </Button>
              </div>
              {generatedCodebitorLink && (
                <div className="flex items-center gap-2">
                  <Input readOnly value={generatedCodebitorLink} className="text-sm bg-white" data-testid="codebitor-link-value" />
                  <Button type="button" variant="outline" onClick={() => { navigator.clipboard?.writeText(generatedCodebitorLink); toast({ title: "Copiat!", description: "Linkul a fost copiat în clipboard." }); }} data-testid="copy-codebitor-link-btn">
                    Copiază
                  </Button>
                </div>
              )}
            </div>

            {formData.codebitors.hasCodebitors && (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border" style={{ backgroundColor: `${headerColor}10`, borderColor: `${headerColor}30` }}>
                  <div>
                    <p className="font-medium" style={{ color: headerColor }}>Adăugați codebitori</p>
                    <p className="text-sm text-gray-600">Puteți adăuga maximum 2 codebitori</p>
                  </div>
                  <Button
                    type="button"
                    onClick={addCodebitor}
                    disabled={formData.codebitors.codebitorsList.length >= 2}
                    style={{ backgroundColor: headerColor }}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Adaugă Codebitor
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {formData.codebitors.codebitorsList.map((codebitor, index) => (
        <CodebitorSection
          key={codebitor.id}
          codebitorIndex={index + 1}
          data={codebitor}
          onChange={(updatedData) => updateCodebitor(codebitor.id, updatedData)}
          onRemove={() => removeCodebitor(codebitor.id)}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header cu branding */}
      <div className="shadow-sm border-b" style={{ backgroundColor: headerColor }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              {brokerData?.logoUrl ? (
                <img src={brokerData.logoUrl} alt={brokerData.companyName} className="h-12 w-auto" />
              ) : (
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Building className="h-6 w-6 text-white" />
                </div>
              )}
              <div className="text-white">
                <h1 className="text-2xl font-bold">{brokerData?.companyName || "Formular Credit"}</h1>
                <p className="text-sm opacity-90">
                  {isCodebitorMode
                    ? `Completare date CODEBITOR pentru: ${codebitorInfo?.applicantName || '—'}`
                    : (inviteInfo?.clientName ? `Pentru: ${inviteInfo.clientName}` : "Completați toate câmpurile obligatorii")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {brokerData?.cui && (
                <div className="text-white/80 text-sm text-right hidden md:block">
                  <p>CUI: {brokerData.cui}</p>
                  {brokerData.isAnpcIntermediary && (
                    <p className="text-xs">Intermediar ANPC avizat</p>
                  )}
                </div>
              )}
              <a 
                href="/login" 
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                🔐 Admin / Login
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {visibleSteps.map((step) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = visibleSteps.findIndex(s => s.id === step.id) < currentVisibleIndex;
            
            return (
              <Badge 
                key={step.id}
                variant={isActive ? "default" : isCompleted ? "secondary" : "outline"}
                className="px-3 py-2 cursor-pointer transition-all"
                style={isActive ? { backgroundColor: headerColor } : {}}
                onClick={() => setCurrentStep(step.id)}
              >
                <Icon className="h-4 w-4 mr-1" />
                {step.title}
              </Badge>
            );
          })}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Avertizare anti-fraudă — afișată la început (primul pas) */}
          {currentVisibleIndex === 0 && (
            <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4" data-testid="anti-fraud-warning-start">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                <div className="text-sm text-red-900">
                  <p className="font-semibold mb-1">Atenție — Siguranța dumneavoastră</p>
                  <p>
                    Fiți vigilent(ă)! Dacă sunteți contactat(ă) telefonic de persoane care se prezintă drept
                    intermediari sau angajați ai băncii și vă cer date personale, parole, coduri sau plăți,
                    <strong> nu le furnizați</strong>. Contactați direct intermediarul
                    <strong> {brokerData?.companyName || ""}</strong>
                    {brokerData?.phone ? <> la <strong>{brokerData.phone}</strong></> : (brokerData?.email ? <> la <strong>{brokerData.email}</strong></> : null)}
                    {" "}pentru a verifica autenticitatea solicitării.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Buton „sar peste declarații" — doar pe linkul complet, pe pașii de acord */}
          {!isSignMode && !isCodebitorMode && [0, 11, 1].includes(currentStep) && (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                onClick={() => setCurrentStep(2)}
                data-testid="skip-consents-btn"
              >
                Am completat deja aceste documente — sar peste ele →
              </Button>
            </div>
          )}

          {/* Pas „Date Identificare" — doar în modul semnături */}
          {currentStep === 200 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><User className="h-5 w-5" /> Date de identificare</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">
                  Completați datele dumneavoastră de identificare. Formularul financiar va fi completat de intermediar; dumneavoastră trebuie doar să citiți și să semnați documentele legale.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nume *</Label>
                    <Input value={formData.personalInfo.lastName || ""} onChange={(e) => updateFormData('personalInfo', { lastName: e.target.value })} data-testid="sign-lastname" />
                  </div>
                  <div>
                    <Label>Prenume *</Label>
                    <Input value={formData.personalInfo.firstName || ""} onChange={(e) => updateFormData('personalInfo', { firstName: e.target.value })} data-testid="sign-firstname" />
                  </div>
                  <div>
                    <Label>Județ</Label>
                    <Input value={formData.personalInfo.domCounty || ""} onChange={(e) => updateFormData('personalInfo', { domCounty: e.target.value })} data-testid="sign-county" />
                  </div>
                  <div>
                    <Label>Localitate *</Label>
                    <Input value={formData.personalInfo.domCity || ""} onChange={(e) => updateFormData('personalInfo', { domCity: e.target.value })} data-testid="sign-city" />
                  </div>
                  <div>
                    <Label>Stradă *</Label>
                    <Input value={formData.personalInfo.domStreet || ""} onChange={(e) => updateFormData('personalInfo', { domStreet: e.target.value })} data-testid="sign-street" />
                  </div>
                  <div>
                    <Label>Număr</Label>
                    <Input value={formData.personalInfo.domNumber || ""} onChange={(e) => updateFormData('personalInfo', { domNumber: e.target.value })} data-testid="sign-number" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {currentStep === 0 && (
            <ConsumerInformationSection 
              data={formData.consumerInformation}
              onChange={(data) => updateFormData('consumerInformation', data)}
              brokerData={brokerData}
            />
          )}
          {currentStep === 1 && (
            <GDPRInformationSection 
              data={formData.gdprInformation}
              onChange={(data) => updateFormData('gdprInformation', data)}
              brokerData={brokerData}
            />
          )}
          {currentStep === 2 && renderCreditTypeSection()}
          {currentStep === 3 && renderCreditInfoSection()}
          {currentStep === 4 && renderPropertySection()}
          {currentStep === 5 && (
            <PersonalInfoSection 
              data={formData.personalInfo}
              onChange={(data) => updateFormData('personalInfo', data)}
              fieldErrors={fieldErrors}
            />
          )}
          {currentStep === 6 && (
            <BankAffiliatedPersonsSection 
              data={formData.bankAffiliatedPersons}
              onChange={(data) => updateFormData('bankAffiliatedPersons', data)}
            />
          )}
          {currentStep === 7 && (
            <EmployerInfoSection 
              data={formData.employerInfo}
              onChange={(data) => updateFormData('employerInfo', data)}
              fieldErrors={fieldErrors}
            />
          )}
          {currentStep === 8 && (
            <IncomeSection 
              data={formData.income}
              onChange={(data) => updateFormData('income', data)}
            />
          )}
          {currentStep === 9 && (
            <DebtSection 
              data={formData.debts}
              onChange={(data) => updateFormData('debts', data)}
            />
          )}
          {currentStep === 10 && renderCodebitorsSection()}
          {currentStep === 11 && (
            <ConsumerDeclarationSection 
              data={formData.consumerDeclaration}
              onChange={(data) => updateFormData('consumerDeclaration', data)}
              personalInfo={formData.personalInfo}
              brokerData={brokerData}
            />
          )}

          {/* Avertizare anti-fraudă — afișată din nou la final */}
          {currentVisibleIndex === visibleSteps.length - 1 && (
            <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4" data-testid="anti-fraud-warning-end">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                <div className="text-sm text-red-900">
                  <p className="font-semibold mb-1">Reamintire — Fiți vigilent(ă)</p>
                  <p>
                    Nu furnizați date, parole, coduri sau plăți persoanelor care vă contactează telefonic pretinzând
                    că sunt intermediari sau de la bancă. La orice suspiciune, sunați intermediarul
                    <strong> {brokerData?.companyName || ""}</strong>
                    {brokerData?.phone ? <> la <strong>{brokerData.phone}</strong></> : (brokerData?.email ? <> la <strong>{brokerData.email}</strong></> : null)}.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => goToVisibleIndex(currentVisibleIndex - 1)}
              disabled={currentVisibleIndex === 0}
            >
              Înapoi
            </Button>
            
            <span className="text-sm text-gray-600">
              Pasul {currentVisibleIndex + 1} din {visibleSteps.length}
            </span>
            
            {currentVisibleIndex < visibleSteps.length - 1 ? (
              <Button type="button" onClick={handleNext} style={{ backgroundColor: headerColor }}>
                Înainte
              </Button>
            ) : (
              <div className="flex flex-wrap gap-3 justify-end">
                {!isSignMode && (
                <Button
                  type="button"
                  onClick={handleAutoOffer}
                  disabled={isGeneratingOffers || isSubmitting}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="auto-offer-btn"
                >
                  {isGeneratingOffers ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generăm ofertele...
                    </>
                  ) : (
                    "🏦 Generează Automat Ofertele"
                  )}
                </Button>
                )}
                <Button 
                  type="submit" 
                  className="min-w-[200px]"
                  style={{ backgroundColor: "#16a34a" }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Procesând...
                    </>
                  ) : (
                    isSignMode ? "📄 Trimite Acordurile Semnate" : "📄 Generează PDF și Trimite"
                  )}
                </Button>
              </div>
            )}
          </div>
        </form>

        {/* Footer cu datele brokerului */}
        <div className="mt-12 pt-6 border-t text-center text-sm text-gray-500">
          <p>Formular furnizat de <strong>{brokerData?.companyName}</strong></p>
          {brokerData?.email && <p>Contact: {brokerData.email}</p>}
          {brokerData?.website && <p>Website: {brokerData.website}</p>}
        </div>
      </div>
    </div>
  );
};

export default PublicLoanForm;
